import { useCallback, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { notification } from 'antd'
import { downloadJSONasCSV } from 'helpers/download-file'
import { batchUpdatePaymentAssignmentStatus } from '../../api/payment-assignment-api-client'
import type {
  BatchUpdatePaymentAssignmentStatusErrorDto,
  BatchUpdatePaymentAssignmentStatusItemDto,
} from '../../api/dto/batch-update-payment-assignment-status.dto'
import { PaymentAssignmentStatusId } from '../PaymentAssignment/Utils/types'
import {
  StatusUpdateFormKey,
  buildStatusMetadata,
} from '../PaymentAssignment/CaseManagement/statusUpdate.type'
import { getDefaultPaymentPeriod } from '../PaymentAssignment/hooks/usePaymentAssignmentFilters'
import {
  useAllowedBulkStatuses,
  CHUNK_SIZE,
  MAX_CSV_ROWS,
  LABEL_TO_FIELD_KEY,
  isAllowedStatus,
} from './bulkStatus.constants'
import {
  buildCsvTemplateRows,
  computeDuplicateBenefitIds,
  isDateField,
  isValidRow,
  normalizeRowFieldOptions,
  parseDateField,
  rowFieldValues,
} from './bulkStatus.helpers'
import type {
  BatchProgress,
  ChunkError,
  ParsedRow,
  Step,
  SubmitLog,
} from './bulkStatus.types'

/**
 * Owns all state and mutation/submit logic for the bulk payment-assignment
 * status upload flow (upload -> preview -> submit). The two step components
 * (`UploadStep`, `PreviewStep`) are purely presentational and receive
 * everything they need via this hook's return value.
 */
export function useBulkStatusUpload() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const allowedStatuses = useAllowedBulkStatuses()
  const [step, setStep] = useState<Step>('upload')
  const [period, setPeriod] = useState<Dayjs>(() =>
    dayjs(getDefaultPaymentPeriod(), 'YYYY-MM').startOf('month')
  )
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  // Per-row backend error messages from the last submit attempt, keyed by row key.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  // Summary of the last submit attempt, shown inline on the preview page.
  const [submitLog, setSubmitLog] = useState<SubmitLog | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const cancelledRef = useRef(false)
  const [batchProgress, setBatchProgress] = useState<BatchProgress>({
    sent: 0,
    total: 0,
    currentBatch: 0,
    totalBatches: 0,
  })

  const nextRowKeyRef = useRef(0)
  const allocateKey = useCallback(() => `r-${nextRowKeyRef.current++}`, [])
  const makeEmptyRow = useCallback(
    (): ParsedRow => ({
      key: allocateKey(),
      benefitId: '',
      status: '',
      note: '',
      fields: {},
    }),
    [allocateKey]
  )

  // ---------- template download ----------
  const handleDownloadTemplate = useCallback(() => {
    const exampleRows = buildCsvTemplateRows(allowedStatuses)
    downloadJSONasCSV(
      exampleRows,
      'payment-assignment-bulk-status-template.csv'
    )
  }, [allowedStatuses])

  // ---------- CSV parse ----------
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      setParseError(null)

      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.errors.length > 0) {
            setParseError(
              `CSV parse error: ${result.errors.map((err) => err.message).join(', ')}`
            )
            return
          }

          const parsed: ParsedRow[] = result.data.map((raw) => {
            const benefitId = raw['benefitId']?.trim() ?? ''
            const rawStatus = (raw['status']?.trim() ?? '').toUpperCase()
            const note = raw['note']?.trim() ?? ''
            const fields: Partial<Record<StatusUpdateFormKey, string>> = {}
            // Accept headers by human-readable label OR raw key (case-insensitive)
            for (const [colHeader, colValue] of Object.entries(raw)) {
              const fieldKey = LABEL_TO_FIELD_KEY[colHeader.toLowerCase()]
              if (fieldKey) {
                let v = (colValue as string | undefined)?.trim()
                // Normalize status-specific date field columns:
                // Accepts MM-DD-YYYY or MM/DD/YYYY, outputs MM-DD-YYYY with leading zeros
                // (e.g., "7/31/2026" -> "07-31-2026")
                if (v && isDateField(fieldKey)) {
                  v = parseDateField(v)
                }
                if (v) fields[fieldKey] = v
              }
            }
            const row: ParsedRow = {
              key: allocateKey(),
              benefitId,
              status: isAllowedStatus(rawStatus, allowedStatuses)
                ? rawStatus
                : (rawStatus as PaymentAssignmentStatusId | ''),
              note,
              fields,
            }
            // Coerce option-backed fields (e.g. Yes/No selects) to their
            // canonical casing regardless of how they were cased in the CSV.
            return normalizeRowFieldOptions(row)
          })

          if (parsed.length === 0) {
            setParseError('CSV has no data rows.')
            return
          }
          if (parsed.length > MAX_CSV_ROWS) {
            setParseError(
              `CSV contains ${parsed.length} rows. Maximum allowed is ${MAX_CSV_ROWS.toLocaleString()}.`
            )
            return
          }

          setRows(parsed)
          setStep('preview')
        },
        error: (err: Error) =>
          setParseError(`Failed to read file: ${err.message}`),
      })
      e.target.value = ''
    },
    [allocateKey]
  )

  // ---------- row mutations ----------
  const clearRowError = useCallback(
    (key: string) =>
      setRowErrors((prev) => {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      }),
    []
  )

  const setRowField = useCallback(
    (key: string, patch: Partial<Omit<ParsedRow, 'key'>>) => {
      setRows((curr) =>
        curr.map((r) => (r.key === key ? { ...r, ...patch } : r))
      )
      clearRowError(key)
    },
    [clearRowError]
  )

  const setRowDynamicField = useCallback(
    (key: string, fieldKey: StatusUpdateFormKey, value: string) => {
      setRows((curr) =>
        curr.map((r) =>
          r.key === key
            ? { ...r, fields: { ...r.fields, [fieldKey]: value } }
            : r
        )
      )
      clearRowError(key)
    },
    [clearRowError]
  )

  const handleAddRow = useCallback(
    () => setRows((curr) => [...curr, makeEmptyRow()]),
    [makeEmptyRow]
  )

  const handleDeleteRow = useCallback(
    (key: string) => setRows((curr) => curr.filter((r) => r.key !== key)),
    []
  )

  const handleAddManually = useCallback(() => {
    setParseError(null)
    setRows([makeEmptyRow()])
    setStep('preview')
  }, [makeEmptyRow])

  const handleReset = useCallback(() => {
    cancelledRef.current = true
    setIsSubmitting(false)
    setIsCancelling(false)
    setStep('upload')
    setRows([])
    setSubmitLog(null)
    setRowErrors({})
    setParseError(null)
    setBatchProgress({ sent: 0, total: 0, currentBatch: 0, totalBatches: 0 })
  }, [])

  const handleCancelSubmit = useCallback(() => {
    cancelledRef.current = true
    setIsCancelling(true)
  }, [])

  // ---------- submit ----------
  const handleConfirm = useCallback(async () => {
    const duplicateBenefitIds = computeDuplicateBenefitIds(rows)
    const validRows = rows.filter((row) =>
      isValidRow(row, duplicateBenefitIds, allowedStatuses)
    )
    const skippedCount = rows.length - validRows.length

    // Map benefitId -> row key so backend results can be mapped
    // back to the exact rows that were submitted. Use trimmed IDs to match
    // what the backend receives and returns.
    const keyByBenefitId = new Map<string, string>()
    validRows.forEach((row) =>
      keyByBenefitId.set(row.benefitId.trim(), row.key)
    )

    const items: BatchUpdatePaymentAssignmentStatusItemDto[] = validRows.map(
      (row) => {
        const status = row.status as PaymentAssignmentStatusId
        return {
          benefitId: row.benefitId.trim(),
          status,
          note: row.note || undefined,
          metadata: buildStatusMetadata(status, rowFieldValues(row)),
        }
      }
    )

    const totalBatches = Math.ceil(items.length / CHUNK_SIZE)
    cancelledRef.current = false
    setIsCancelling(false)
    setIsSubmitting(true)
    setBatchProgress({
      sent: 0,
      total: items.length,
      currentBatch: 1,
      totalBatches,
    })

    const successIds: string[] = []
    const failures: BatchUpdatePaymentAssignmentStatusErrorDto[] = []
    const chunkErrors: ChunkError[] = []

    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
      if (cancelledRef.current) break

      setBatchProgress((prev) => ({
        ...prev,
        currentBatch: Math.floor(i / CHUNK_SIZE) + 1,
      }))

      const chunk = items.slice(i, i + CHUNK_SIZE)
      try {
        const response = await batchUpdatePaymentAssignmentStatus({
          month: period.month(),
          year: period.year(),
          items: chunk,
        })
        successIds.push(...(response.results ?? []))
        failures.push(...(response.errors ?? []))
      } catch (error: any) {
        const raw =
          error.response?.data?.message ||
          error.message ||
          'Batch request failed'
        const msg = typeof raw === 'string' ? raw : JSON.stringify(raw)
        chunkErrors.push({
          benefitIds: chunk.map((item) => item.benefitId),
          message: msg,
        })
      }

      setBatchProgress((prev) => ({
        ...prev,
        sent: Math.min(i + CHUNK_SIZE, items.length),
      }))
    }

    setIsSubmitting(false)

    if (
      cancelledRef.current &&
      successIds.length === 0 &&
      failures.length === 0 &&
      chunkErrors.length === 0
    ) {
      return
    }

    // Build a benefitId -> message map for all failures (per-item + chunk-level).
    const failureMessageByBenefitId = new Map<string, string>()
    failures.forEach((f) =>
      failureMessageByBenefitId.set(f.benefitId, f.message)
    )
    chunkErrors.forEach((c) =>
      c.benefitIds.forEach((id) => failureMessageByBenefitId.set(id, c.message))
    )

    const successSet = new Set(successIds.map((id) => id))

    // Remove successfully-updated rows from the list; keep invalid rows and
    // failed rows so the user can fix and resubmit without leaving the page.
    setRows((prev) => prev.filter((r) => !successSet.has(r.benefitId.trim())))

    // Attach backend error messages to the rows that failed, keyed by row key.
    const nextRowErrors: Record<string, string> = {}
    failureMessageByBenefitId.forEach((message, benefitId) => {
      const key = keyByBenefitId.get(benefitId)
      if (key) nextRowErrors[key] = message
    })
    setRowErrors(nextRowErrors)

    const failCount = failureMessageByBenefitId.size
    setSubmitLog({
      successCount: successIds.length,
      successIds,
      skippedCount,
      failures: Array.from(failureMessageByBenefitId.entries()).map(
        ([benefitId, message]) => ({ benefitId, message })
      ),
    })

    if (successIds.length > 0) {
      notification.success({
        message: `Updated ${successIds.length} benefit(s)`,
      })
    }
    if (failCount > 0) {
      notification.warning({ message: `${failCount} item(s) failed` })
    }
  }, [rows, period])

  const validCount = useMemo(() => {
    const duplicateBenefitIds = computeDuplicateBenefitIds(rows)
    return rows.filter((row) =>
      isValidRow(row, duplicateBenefitIds, allowedStatuses)
    ).length
  }, [rows, allowedStatuses])
  const invalidCount = rows.length - validCount

  return {
    // refs
    fileInputRef,
    // state
    step,
    period,
    rows,
    parseError,
    rowErrors,
    submitLog,
    isSubmitting,
    isCancelling,
    batchProgress,
    validCount,
    invalidCount,
    allowedStatuses,
    // setters
    setPeriod,
    // handlers
    handleDownloadTemplate,
    handleFileChange,
    handleAddRow,
    handleDeleteRow,
    handleAddManually,
    handleReset,
    handleConfirm,
    handleCancelSubmit,
    setRowField,
    setRowDynamicField,
  }
}
